import { useRouter } from 'next/router'
import {
  dehydrate,
  encodeSpecialCharacters,
  IContentfulResponse
} from '@smuchile/pkg-unimarc-hooks'
import { fetchBannerSearch } from 'shared/services/fetchBannerSearch'
import { adaptBannerToContentfulFormat } from 'shared/helpers/bannerAdapter'
import {
  ProductSearchPage,
  ResponsiveContext,
  MetaHead,
  ErrorPageV2Props
} from 'components'
import {
  cleanPageNumber,
  cleanSearching,
  getHeaderCookies,
  getSearchData,
  isURL
} from 'shared/helpers'
import { NextPageWithLayout } from 'shared/interfaces/Layout'
import dynamic from 'next/dynamic'
import { LayoutAndErrorBundary } from 'components/LayoutAndErrorBundary'
import CurtainWrapper from 'components/CurtainWrapper'

const ErrorPage = dynamic<ErrorPageV2Props>(
  import('components').then((mod) => {
    return mod.ErrorPageV2
  })
)

export interface SearchProps {
  bannerLegal: IContentfulResponse
  error: boolean
  isMobile: boolean
  page: number
  query: string
  statusCode: number
}

const Search: NextPageWithLayout = ({
  bannerLegal,
  isMobile,
  page,
  query,
  statusCode
}: SearchProps): React.ReactElement => {
  const router = useRouter()

  if (statusCode === 500) {
    return <ErrorPage errorType='500' />
  }

  return (
    <ResponsiveContext isMobile={isMobile}>
      <>
        <MetaHead
          canonicalPath={cleanPageNumber(router?.asPath)}
          type='SRP'
        />
        <ProductSearchPage
          bannerLegal={bannerLegal}
          isMobile={isMobile}
          key={query}
          page={page}
          search={query}
        />
      </>
    </ResponsiveContext>
  )
}

Search.getLayout = (page) => {
  return (
    <CurtainWrapper>
      <LayoutAndErrorBundary>{page}</LayoutAndErrorBundary>
    </CurtainWrapper>
  )
}

export async function getServerSideProps(context) {
  const query = context?.query
  if (!query?.q) {
    return {
      props: {},
      notFound: true
    }
  }

  let page = parseInt(query?.page) || 1
  const headers = getHeaderCookies(context)
  const { intelliSearchData, queryClient } = await getSearchData({
    query: encodeSpecialCharacters(query?.q),
    page,
    headers,
    params: query
  })
  const urlRedirect = intelliSearchData?.['data']?.['redirect']?.toString()
  // redirects
  if (isURL({ url: urlRedirect })) {
    return {
      redirect: {
        permanent: false,
        destination: `/${urlRedirect?.split('/')?.slice(3)?.join('/')}`
      }
    }
  }
  //  Error 500
  if (intelliSearchData?.['error'] || !context?.query) {
    return {
      props: {
        statusCode: 500
      }
    }
  }

  // Error 404
  if (intelliSearchData?.['error']) {
    return {
      props: null,
      notFound: true
    }
  }

  // Get data to banner legal
  const bannerSearchData = await fetchBannerSearch({
    type: 'search',
    value: query?.q,
    headers
  })
  const bannerLegal = adaptBannerToContentfulFormat(bannerSearchData)

  if (query?.page) {
    page = parseInt(query?.page)
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      intelliSearchData: intelliSearchData || null,
      bannerLegal,
      page,
      query: cleanSearching(query?.q),
      statusCode: null
    }
  }
}

export default Search
